import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {updateFeedBrief} from 'common/src/modules/contentFeeds/store/contentFeedPage/thunks/updateFeedBrief.thunk';
import assertAiGeneratedContentFeed, {
  CONTENT_FEED_AI_TOOL_CONTEXT_ERROR,
} from '../utils/assertAiGeneratedContentFeed';
import {ContentFeedAiGeneratedSkillToolName} from '../skills/contentFeedAiGeneratedSkill.enums';

const inputSchema = z.object({
  objective: z.string().optional().nullable().describe('The campaign objective or goal'),
  description: z
    .string()
    .optional()
    .nullable()
    .describe(
      'Detailed description of the campaign creative requirements, focusing on elements needed to be generated by AI into the content feed',
    ),
  applyBrandGuidelines: z
    .boolean()
    .optional()
    .nullable()
    .describe('Whether to apply brand guidelines during content generation'),
});

export default function updateContentFeedBriefTool(store: CLStore) {
  async function execute({
    input: {objective, description, applyBrandGuidelines},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const feed = state.contentFeedPage?.feed;

    if (!assertAiGeneratedContentFeed(feed)) {
      return toolError(CONTENT_FEED_AI_TOOL_CONTEXT_ERROR);
    }

    const currentBrief = feed.brief;

    const updatedBrief = {
      objective: currentBrief?.objective ?? null,
      description: currentBrief?.description ?? null,
      targetPlatforms: currentBrief?.targetPlatforms ?? null,
      personaIds: currentBrief?.personaIds ?? null,
      applyBrandGuidelines: currentBrief?.applyBrandGuidelines ?? false,
      ...(objective !== undefined && objective !== null && {objective}),
      ...(description !== undefined && description !== null && {description}),
      ...(applyBrandGuidelines !== undefined &&
        applyBrandGuidelines !== null && {applyBrandGuidelines}),
    };

    try {
      await store
        .dispatch(updateFeedBrief({feedId: feed.id, brief: updatedBrief}))
        .unwrap();

      const updatedFields: string[] = [];

      if (objective !== undefined && objective !== null) {
        updatedFields.push('objective');
      }

      if (description !== undefined && description !== null) {
        updatedFields.push('description');
      }

      if (applyBrandGuidelines !== undefined && applyBrandGuidelines !== null) {
        updatedFields.push('applyBrandGuidelines');
      }

      return toolResult(
        `Successfully updated content feed brief. Updated fields: ${updatedFields.join(', ')}.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error updating brief: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Updating content feed brief',
    name: ContentFeedAiGeneratedSkillToolName.UpdateContentFeedBrief,
    description:
      'Updates the AI content brief for the current AI-generated content feed (objective, description, apply brand guidelines).',
    execute,
    inputSchema,
  });
}
